import { AnyAction } from 'redux';
import { GlobalActions, isAzam } from '@ps20-frontend/shop-integration-redux';
import {
  Role,
  VermittlerFom,
  Vermittlernummer,
  Vertreter as VermittlerBff,
} from '@ps20/bff-models';

import { IAppState } from '@ps20-frontend/apps/checkout-store';
import {
  AktionsNachlass,
  convertVermittlernummerToString,
  DIPLOMATEN_CODE,
} from '@ps20-frontend/frontend-models';
import { CheckoutActions } from '../checkout.actions';
import { FREMD_VTNR_AAWORLD_MAPPING } from '../selector/vermittlernummer.selector';

export interface Vermittler {
  vorname: string;
  nachname: string;
  telefon: string;
  selectedVermittlernummer: {
    eigen: Vermittlernummer;
    fremd: Vermittlernummer;
    mappedFromActionCode?: Vermittlernummer;
  };
  vermittlernummern: Vermittlernummer[];
  hasMoreThanFiftyRoles?: boolean;
  bnrb: string;
  bnrbs: string[];
  bsvtnrs: string[];
  fremd?: boolean;
  fom: string;
  fremdvorgangsnummer?: string;
  isInkasso?: boolean;
}

export const vermittlerReducer = function (
  sectionState: Vermittler = null,
  action: AnyAction,
  state: IAppState,
): Vermittler {
  switch (action.type) {
    case GlobalActions.VERMITTLER_FREMD_CHANGED: {
      return {
        ...sectionState,
        fremd: action.payload.fremd,
        fom: null,
        selectedVermittlernummer: {
          ...sectionState.selectedVermittlernummer,
          fremd: {
            ...sectionState.selectedVermittlernummer.fremd,
            vermittlerFom: getVermittlerFom(
              state?.transientState?.role,
              sectionState.selectedVermittlernummer.fremd.vermittlerFom,
            ),
          },
        },
      };
    }
    case GlobalActions.VERMITTLER_NUMMER_CHANGED: {
      const oldVermittlernummer = sectionState.fremd
        ? sectionState.selectedVermittlernummer.fremd
        : sectionState.selectedVermittlernummer.eigen;
      let newVermittlerNummer = {};
      if (sectionState.fremd || isAzam(state?.transientState?.role)) {
        newVermittlerNummer = {
          ...oldVermittlernummer,
          geschaeftsstelle: action.payload.vermittlernummer.geschaeftsstelle,
          zweigniederlassung: action.payload.vermittlernummer.zweigniederlassung,
          vertreternummer: action.payload.vermittlernummer.vertreternummer,
        };
      } else {
        newVermittlerNummer = sectionState.vermittlernummern.find(
          vermittlernummer =>
            vermittlernummer.zweigniederlassung ===
              action.payload.vermittlernummer.zweigniederlassung &&
            vermittlernummer.geschaeftsstelle ===
              action.payload.vermittlernummer.geschaeftsstelle &&
            vermittlernummer.vertreternummer === action.payload.vermittlernummer.vertreternummer,
        );
      }

      if (sectionState.fremd) {
        return {
          ...sectionState,
          selectedVermittlernummer: {
            ...sectionState.selectedVermittlernummer,
            fremd: newVermittlerNummer as Vermittlernummer,
          },
        };
      } else {
        return {
          ...sectionState,
          selectedVermittlernummer: {
            ...sectionState.selectedVermittlernummer,
            eigen: newVermittlerNummer as Vermittlernummer,
          },
        };
      }
    }
    case GlobalActions.VERMITTLER_BNRB_CHANGED: {
      return { ...sectionState, bnrb: action.payload.bnrb };
    }
    case CheckoutActions.VERMITTLER_FOM_RESPONSE_MAPPED: {
      if (sectionState.fremd) {
        return {
          ...sectionState,
          selectedVermittlernummer: {
            ...sectionState.selectedVermittlernummer,
            fremd: {
              ...sectionState.selectedVermittlernummer.fremd,
              vermittlerFom: action.payload.vermittlernummerWithFom.vermittlerFom,
            },
          },
        };
      }
      return {
        ...sectionState,
        selectedVermittlernummer: {
          ...sectionState.selectedVermittlernummer,
          eigen: {
            ...sectionState.selectedVermittlernummer.eigen,
            vermittlerFom: action.payload.vermittlernummerWithFom.vermittlerFom,
          },
        },
      };
    }
    case CheckoutActions.VERMITTLER_FOM_CHANGED: {
      return { ...sectionState, fom: action.payload.fom };
    }
    case CheckoutActions.VERMITTLER_FREMDVORGANGSNUMMER_CHANGED: {
      return {
        ...sectionState,
        fremdvorgangsnummer:
          action.payload.fremdvorgangsnummer === '' ? null : action.payload.fremdvorgangsnummer,
      };
    }
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      let newState = sectionState;
      const vermittlerBff: VermittlerBff = action.payload.vertreter;
      const vtnrDefault: Vermittlernummer = action.payload.vertreter.defaultVermittlernummer;
      if (vtnrDefault) {
        if (
          vtnrDefault.geschaeftsstelle &&
          vtnrDefault.vertreternummer &&
          vtnrDefault.zweigniederlassung
        ) {
          newState = {
            ...newState,
            ...{
              selectedVermittlernummer: {
                ...newState.selectedVermittlernummer,
                eigen: vtnrDefault,
              },
              vorbelegt: true,
            },
          };
        }
      }
      const bnrbDefault: string = action.payload.vertreter.defaultBnrb;
      if (bnrbDefault) {
        newState = { ...newState, ...{ bnrb: bnrbDefault, vorbelegt: true } };
      }
      if (vermittlerBff.nachname) {
        newState.nachname = vermittlerBff.nachname;
      }
      if (vermittlerBff.vorname) {
        newState.vorname = vermittlerBff.vorname;
      }
      if (vermittlerBff.vermittlernummern && vermittlerBff.vermittlernummern.length > 0) {
        newState.vermittlernummern = vermittlerBff.vermittlernummern;
      }
      if (vermittlerBff.bnrbs && vermittlerBff.bnrbs.length > 0) {
        newState.bnrbs = vermittlerBff.bnrbs;
      }
      if (vermittlerBff.bsvtnrs && vermittlerBff.bsvtnrs.length > 0) {
        newState.bsvtnrs = vermittlerBff.bsvtnrs;
      }
      if (vermittlerBff.hasMoreThanFiftyRoles != null) {
        newState.hasMoreThanFiftyRoles = vermittlerBff.hasMoreThanFiftyRoles;
      }
      newState.isInkasso = !!vermittlerBff.isInkasso;
      return newState;
    }
    case GlobalActions.UPDATE_AKTIONSNACHLASS_CODE: {
      const actionPayload: AktionsNachlass = action.payload;
      const isAAWorld = actionPayload.code === DIPLOMATEN_CODE;

      const vermittlernummerToString = convertVermittlernummerToString(
        sectionState.selectedVermittlernummer.eigen,
      );

      const shouldMapVtnr = isAAWorld && FREMD_VTNR_AAWORLD_MAPPING.has(vermittlernummerToString);

      let mappedFromActionCode: Vermittlernummer = null;
      let fremd = false;

      if (shouldMapVtnr) {
        const vtnr = FREMD_VTNR_AAWORLD_MAPPING.get(vermittlernummerToString).split('/');
        mappedFromActionCode = {
          vertreternummer: vtnr[2],
          geschaeftsstelle: vtnr[1],
          zweigniederlassung: vtnr[0],
          vermittlerFom: VermittlerFom.NONE,
        };
        fremd = true;
      }

      return {
        ...sectionState,
        selectedVermittlernummer: {
          ...sectionState.selectedVermittlernummer,
          mappedFromActionCode: mappedFromActionCode,
        },
        fremd,
      };
    }
    default:
      return sectionState;
  }
};

function getVermittlerFom(role: Role, currentFom: VermittlerFom): VermittlerFom {
  if (currentFom != VermittlerFom.NONE) {
    return currentFom;
  }

  if (role === Role.OLB) {
    return VermittlerFom.OLB;
  } else if (role === Role.GENO) {
    return VermittlerFom.GENO;
  } else {
    return VermittlerFom.NONE;
  }
}
